// src/hooks/programs/useToggleFavoriteProgram.js
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toggleProgramFavorite } from "../../api/apiPrograms";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const useToggleFavoriteProgram = () => {
    const queryClient = useQueryClient();
    const { i18n } = useTranslation();
    const isArabic = i18n.language === "ar";

    const { mutate: toggleFavoriteMutate, isLoading: isToggling } = useMutation({
        mutationFn: ({ id, is_favorite }) => toggleProgramFavorite(id, is_favorite),
        onMutate: async ({ id, is_favorite }) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries(["programs"]);

            // Snapshot the previous value
            const previousPrograms = queryClient.getQueryData(["programs"]);

            // Optimistically update to the new value
            queryClient.setQueriesData(["programs"], (old) => {
                if (!old?.data?.items) return old;
                return {
                    ...old,
                    data: {
                        ...old.data,
                        items: old.data.items.map((program) => {
                            if (program.id === id) {
                                return { ...program, is_favorite };
                            }
                            return program;
                        }),
                    },
                };
            });

            // Return a context object with the snapshotted value
            return { previousPrograms };
        },
        onError: (error, newProgram, context) => {
            // Rollback to the previous value
            if (context?.previousPrograms) {
                queryClient.setQueriesData(["programs"], context.previousPrograms);
            }
            toast.error(
                isArabic
                    ? error?.response?.data?.message || "حدث خطأ أثناء تعديل المفضلة"
                    : error?.response?.data?.message || "Failed to update favorite status"
            );
        },
        onSettled: () => {
            // Always refetch after error or success to ensure data is in sync
            queryClient.invalidateQueries(["programs"]);
        },
    });

    return {
        toggleFavoriteMutate,
        isToggling,
    };
};

export default useToggleFavoriteProgram;
